<template>
  <div>
    <div ref="mapContainer" class="map-wrapper" :style="{height: height}"></div>

    <MapDraw
      v-if="enableDraw && map"
      :map="map"
      @shape-drawn="onShapeDrawn"
      @shapes-updated="onShapesUpdated"
    />

    <MapPin
      v-if="enablePins && map"
      :map="map"
      :pins="pins"
      @pin-clicked="onPinClicked"
    />

    <MapKml
      v-if="enableKml && map"
      :map="map"
      :files="kmlFiles"
      @kml-loaded="$emit('kml-loaded', $event)"
      @kml-error="$emit('kml-error', $event)"
    />

    <MapCluster
      v-if="enableCluster && map"
      :map="map"
      :markers="pins"
    />
  </div>
</template>

<script>
import L from 'leaflet';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import MapDraw from './MapDraw.vue';
import MapPin from './MapPin.vue';
import MapKml from './MapKml.vue';
import MapCluster from './MapCluster.vue';

export default {
  name: 'MapBase',
  components: {
    MapDraw,
    MapPin,
    MapKml,
    MapCluster
  },
  props: {
    enableDraw: { type: Boolean, default: false },
    enableKml: { type: Boolean, default: false },
    enableSearch: { type: Boolean, default: false },
    enableCluster: { type: Boolean, default: false },
    enablePins: { type: Boolean, default: false },
    allowPinDrop: { type: Boolean, default: false },
    pins: {
      type: Array,
      default: () => [],
    },
    kmlFiles: { type: Array, default: () => [] },
    center: {
      type: Object,
      default: () => ({ lat: 23.03, lng: 72.58 })
    },
    zoom: {
      type: Number,
      default: 5
    },
    height: {
      type: String,
      default: "500px"
    }
  },
  data() {
    return {
      map: null,
    };
  },
  mounted() {
    this.initMap();
    this.$emit('map-ready', this.map);
  },
  watch: {
    center: {
      handler(newCenter) {
        if (this.map && newCenter && newCenter.lat && newCenter.lng) {
          this.map.flyTo([newCenter.lat, newCenter.lng], this.zoom, { animate: true, duration: 0.5 });
        }
      },
      deep: true,
    },
    pins: {
      handler(newPins) {
        if (!this.map) return;
        // Update pin logic here if needed
      },
      deep: true,
    },
  },
  methods: {
    initMap() {
      this.map = L.map(this.$refs.mapContainer).setView([this.center.lat, this.center.lng], this.zoom);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);

      if (this.enableSearch) {
        this.addSearchControl();
      }

      this.map.on('click', this.onMapClick);
    },
    onShapeDrawn(shape) {
      this.$emit('shape-drawn', shape);
    },
    onShapesUpdated(updated) {
      this.$emit('shapes-updated', updated);
    },
    onPinClicked(pin) {
      this.$emit('pin-clicked', pin);
    },
    addSearchControl() {
      const provider = new OpenStreetMapProvider();

      const searchControl = new GeoSearchControl({
        provider,
        style: 'bar',
        showMarker: true,
        showPopup: true,
        marker: {
          icon: new L.icon({
            iconUrl: require('leaflet/dist/images/marker-icon.png'), // adjust path
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
            shadowSize: [41, 41],
          }),
          draggable: false,
        },
        autoClose: true,
        retainZoomLevel: false,
        animateZoom: true,
        keepResult: true,
      });

      this.map.addControl(searchControl);

      // Emit selected location
      this.map.on('geosearch/showlocation', (result) => {
        this.$emit('address-selected', result.location);
      });
    },
    onMapClick(e) {
      if (this.allowPinDrop) {
        const { lat, lng } = e.latlng;
    
        // Remove old marker if exists
        if (this.clickMarker) {
          this.map.removeLayer(this.clickMarker);
        }
    
        // // Create new marker
        this.clickMarker = L.marker([lat, lng], {
          icon: this.getCustomIcon()
        }).addTo(this.map);
    
        // // Optional popup text
        this.clickMarker.bindPopup(
          `Latitude: ${lat.toFixed(5)}, Longitude: ${lng.toFixed(5)}`
        ).openPopup();
    
        // // Emit to parent if needed
        this.$emit('map-clicked', { lat, lng });

      }
    },
    getCustomIcon() {
      return new L.icon({
        iconUrl: require('leaflet/dist/images/marker-icon.png'), // adjust path
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
        shadowSize: [41, 41],
      })
    },
    clearClickMarker() {
      if (this.clickMarker) {
        this.map.removeLayer(this.clickMarker);
        this.clickMarker = null;
      }
    }
  },

};
</script>
